import React, { useEffect, useState } from 'react';
import { useAppContext } from 'providers/AppProvider';
import { Container, Row, Col, Form, Card, Badge, Button } from 'react-bootstrap';
import axios from 'axios';
import Select from "react-select";
import PatientCard from './PatientCard';
import ShimmerUI from 'components/ShimmerUI/PatientShimmer';
import CommonBreadCrumb from 'components/commonbreadcrumb/CommonBreadCrumb';
import { socket } from '../../socket';
import SetMastersDetails from 'components/common/SetMastersDetails';
import RemoveMasters from 'components/common/RemoveMasters';
import AddOrderDetailModal from './AddOrderDetailModal';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import TopSummaryBar from './TopSummaryBar';
import { storeList } from 'components/common/StoreList';

const PatientView = () => {
  const [storeOptions] = useState([{ key: "Select Store", value: "" }, ...storeList]);
  const [selectedStoreValue, setSelectedStoreValue] = useState('');
  const [cardData, setCardData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [topData, setTopData] = useState({});
  const [filteredCardData, setFilteredCardData] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState({ key: 'ALL FACILITIES', value: '' });
  let results = JSON.parse(localStorage.getItem("facilitiesData"));
  const [options] = useState([
    { key: 'ALL FACILITIES', value: '' },
    ...results
  ]);

  const columnDefinitions = [
    { key: '2', title: 'Intake' },
    { key: '3', title: 'Data Entry' },
    { key: '4', title: 'PV1' },
    { key: '5', title: 'Filling' },
    { key: '6', title: 'PV2' },
    { key: '7', title: 'Manifest' },
  ];
  const computedColumns = columnDefinitions.map((column) => {
    let count = 0;
    if (filteredCardData && filteredCardData[column.key]) {
      Object.values(filteredCardData[column.key]).forEach((patient) => {
        if (patient.orders) {
          count += patient.orders.length;
        }
      });
    }
    return { ...column, count };
  });


  const {
    config: { isFluid, isNavbarVerticalCollapsed },
    setConfig
  } = useAppContext();

  const addOrderModal = () => {
    setShowModal(true);
  };

  useEffect(() => {
    setConfig('isFluid', true);
    setConfig('isNavbarVerticalCollapsed', true);

    async function onConnect() {
      console.log('Connected to server');
      await getPatientViewData();
    }

    function onDisconnect() {
      console.log('Disconnected from server');
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    const tokenStr = localStorage.getItem("accessToken");
    socket.emit('getPatientViewData', { tokenStr });

    socket.on('patientDataUpdated', async () => {
      console.log('Patient data updated via socket.');
      await getPatientViewData();
    });

    getPatientViewData();
    SetMastersDetails();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.emit('clearAllOrders', { tokenStr: tokenStr });
      setConfig('isFluid', false);
      setConfig('isNavbarVerticalCollapsed', false);
      RemoveMasters();
    };
  }, []);

  async function getPatientViewData() {
    let tokenStr = localStorage.getItem("accessToken");
    const { data } = await axios.get(`${process.env.REACT_APP_SERVER_URL}/orders/patient-dashboard`, { headers: { 'Authorization': `Bearer ${tokenStr}` } });
    const dataObj = data.orders;
    setTopData(data.orders_status_count);
    setCardData(dataObj);
    setFilteredCardData(dataObj);
  }

  const handleStoreChange = (selectedOption) => {
    setSelectedStoreValue(selectedOption);
    if (selectedOption.value) {
      let filteredData = {};
      Object.keys(cardData).forEach((statusKey) => {
        let filteredPatients = Object.keys(cardData[statusKey])
          .filter(patientId => (cardData[statusKey][patientId].store_id || 1) === selectedOption.value)
          .reduce((obj, patientId) => {
            obj[patientId] = cardData[statusKey][patientId];
            return obj;
          }, {});

        if (Object.keys(filteredPatients).length > 0) {
          filteredData[statusKey] = filteredPatients;
        }
      });

      setFilteredCardData(filteredData);
    } else {
      setFilteredCardData(cardData);
    }
  }

  const handleFacilityFieldChange = (selectedOption) => {
    setSelectedFacility(selectedOption.value);

    if (selectedOption.value) {
      let filteredData = {};
      Object.keys(cardData).forEach((statusKey) => {
        let filteredPatients = Object.keys(cardData[statusKey])
          .filter(patientId => cardData[statusKey][patientId].facility_id === selectedOption.value)
          .reduce((obj, patientId) => {
            obj[patientId] = cardData[statusKey][patientId];
            return obj;
          }, {});

        if (Object.keys(filteredPatients).length > 0) {
          filteredData[statusKey] = filteredPatients;
        }
      });

      setFilteredCardData(filteredData);
    } else {
      setFilteredCardData(cardData);
    }
  };

  const breadcrumbItems = [
    { name: 'Dashboard', href: '/main', isActive: false },
    { name: 'Patient', href: '#', isActive: true },
    { name: 'Patient Dashboard', href: "#", isActive: true }
  ];

  return (
    <>
      {cardData ? (
        <>
          <Row className="m-00">
            <AddOrderDetailModal show={showModal} setShow={setShowModal} />
            <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />
            <Card className="p-1 pt-3 pb-1">
              <Row className='mb-3'>
                <TopSummaryBar stats={topData} />
              </Row>
            </Card>
            <Card className="p-3 pt-3 pb-2 mb-4">
              <Row className='mb-3'>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Facility</Form.Label>
                    <ThemedSelect
                      name="facilityname"
                      closeMenuOnSelect={true}
                      placeholder='Select Facility'
                      classNamePrefix="react-select"
                      options={options}
                      value={options.find(opt => opt.value === selectedFacility) || selectedFacility}
                      getOptionLabel={option => option.key}
                      getOptionValue={option => option.value}
                      onChange={handleFacilityFieldChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Store</Form.Label>
                    <ThemedSelect
                      name="store"
                      closeMenuOnSelect={true}
                      placeholder='Select store'
                      classNamePrefix="react-select"
                      options={storeOptions}
                      value={selectedStoreValue}
                      getOptionLabel={option => option.key}
                      getOptionValue={option => option.value}
                      onChange={(e) => handleStoreChange(e)}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderColor: "#ced4da",
                          boxShadow: "none",
                          "&:hover": { borderColor: "#80bdff" },
                        }),
                        option: (provided, { isFocused, isSelected }) => ({
                          ...provided,
                          backgroundColor: isSelected ? "#e9ecef !important" : isFocused ? "#f8f9fa !important" : "white !important",
                          color: isSelected ? "#212529 !important" : "#212529 !important",
                          cursor: "pointer",
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: "#212529",
                        }),
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={1} className='text-end mt-4'>
                  <Button onClick={addOrderModal} className='w-100 h-100'>Add Order</Button>
                </Col>


                <Col md={3} className="d-flex flex-column align-items-start">
                  <Form.Label>&nbsp;</Form.Label>
                  <div className="d-flex flex-wrap align-items-center gap-4 h-100">
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className="bg-success border rounded"
                        style={{ width: '25px', height: '25px', display: 'inline-block' }}
                      ></span>
                      <span className="fs-11 text-muted">Internal Notes</span>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span
                        className="bg-danger border rounded"
                        style={{ width: '25px', height: '25px', display: 'inline-block' }}
                      />
                      <small className="text-muted">Messages</small>
                    </div>
                  </div>
                </Col>


              </Row>
            </Card>
            {Object.keys(filteredCardData).length > 0 ? (
              computedColumns.map((column) => (
                <Col key={column.key} id={column.key} xs={2} className="dayCols1 px-1">
                  <Card>
                    <Card.Header className="dateCol text-white card-header text-center p-2">
                      {column.title} <Badge bg="success">{column.count}</Badge>
                    </Card.Header>
                    <Card.Body className='p-0'>
                      {filteredCardData[column.key] &&
                        Object.keys(filteredCardData[column.key]).map((patientId) => {
                          const patient = filteredCardData[column.key][patientId];
                          return <PatientCard key={patientId} patient={patient} />
                        })}
                    </Card.Body>
                  </Card>
                </Col>
              ))
            ) : (
              <div className="text-center py-4">
                <h5 className="text-muted">No Data Available</h5>
              </div>
            )}
          </Row>
        </>
      ) : (
        <ShimmerUI />
      )}
    </>
  );
};

export default PatientView;